"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, Mail, Phone, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section id="contact" ref={ref} className={`py-16 px-4 md:px-8 lg:px-16 bg-light-background text-foreground-light transition-all duration-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-display font-semibold text-foreground-light mb-4">
            Get In Touch
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Have questions about our ballet programs? We'd love to hear from you. Reach out to us and we'll respond as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <FormProvider className="w-full" formId="contact-form-contact" sectionName="contact-section" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          message: {
            required: true,
            minLength: 10
          }
        }} submitText="Send Message" successMessage="Thank you for contacting us! We'll get back to you soon.">
            <ContactForm />
          </FormProvider>

          <div className="space-y-8">
            

            <div className="space-y-6 bg-muted-light-background rounded-lg p-8 border border-muted/30">
              <h3 className="text-2xl font-display font-semibold text-foreground-light mb-6">
                Contact Information
              </h3>
              
              <div className="flex items-start gap-4">
                <MapPin className="size-5 text-primary shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-foreground-light mb-1">Address</p>
                  <p className="text-muted-foreground">9761 Samoa Ave, Tujunga, CA 91042</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone className="size-5 text-primary shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-foreground-light mb-1">Phone</p>
                  <p className="text-muted-foreground">8182980589</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="size-5 text-primary shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-foreground-light mb-1">Email</p>
                  <p className="text-muted-foreground">info@liveentertainmentllc.com</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light border border-primary/20 rounded-lg">
        <p className="text-primary-dark text-lg mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      },
      maxLength: {
        value: 50,
        message: "Name must not exceed 50 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground-light font-semibold">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Enter your full name" className="border-muted/40 focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-200" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground-light font-semibold">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" className="border-muted/40 focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-200" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required",
      pattern: {
        value: /^[0-9\s\-\+\(\)]+$/,
        message: "Please enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground-light font-semibold">Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(123) 456-7890" className="border-muted/40 focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-200" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground-light font-semibold">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your interest in our programs..." className="min-h-32 border-muted/40 focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-200 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-foreground-accent hover:bg-accent-hover hover:shadow-medium hover:scale-[1.02] active:bg-accent-active active:scale-100 transition-all duration-[150ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] h-11 text-base font-semibold">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : 'Send Message'}
      </Button>
    </Form>;
}